import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import MapContext from './MapContext';

const Map = ({
  apiKey,
  mapId = process.env.REACT_APP_GOOGLE_MAP_ID,
  onMapClick,
  onMapLoad,
  onMarkerClusterLoad,
  onFullScreenButtonClick,
  onFullScreenExit,
  mapOptions = {},
  mapContainerCssStyle,
  mapContainerStyleFullScreen,
  fullScreenIcon,
  children
}) => {
  const googleMapRef = useRef(null);
  const [showFullScreenIcon, setShowFullScreenIcon] = useState(true);
  const [map, setMap] = useState(null);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [isMarkerClusterLoaded, setIsMarkerClusterLoaded] = useState(false);

  useEffect(() => {
    setupComponent();
    
    return () => {
      if (document.removeEventListener) {
        document.removeEventListener('webkitfullscreenchange', exitHandler, false);
        document.removeEventListener('mozfullscreenchange', exitHandler, false);
        document.removeEventListener('fullscreenchange', exitHandler, false);
        document.removeEventListener('MSFullscreenChange', exitHandler, false);
      }
    };
  }, []);

  const setupComponent = () => {
    if (!window.google) {
      const googleMapScript = document.createElement('script');
      // loading=async requires using importLibrary later
      googleMapScript.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&loading=async&libraries=places,marker`;
      googleMapScript.async = true;
      googleMapScript.defer = true;
      window.document.body.appendChild(googleMapScript);
      
      googleMapScript.addEventListener('load', () => {
        loadMarkerClusterer();
      });
      
      // Handle errors just in case
      googleMapScript.addEventListener('error', (e) => {
        console.error("Failed to load Google Maps script:", e);
      });

    } else {
      loadMarkerClusterer();
    }

    if (document.addEventListener) {
      document.addEventListener('webkitfullscreenchange', exitHandler, false);
      document.addEventListener('mozfullscreenchange', exitHandler, false);
      document.addEventListener('fullscreenchange', exitHandler, false);
      document.addEventListener('MSFullscreenChange', exitHandler, false);
    }
  };

  const loadMarkerClusterer = () => {
    if (!window.markerClusterer) {
      const markerClustererScript = document.createElement('script');
      markerClustererScript.src = 'https://unpkg.com/@googlemaps/markerclusterer/dist/index.min.js';
      markerClustererScript.async = true;
      window.document.body.appendChild(markerClustererScript);
      markerClustererScript.addEventListener('load', () => {
        initMap();
        handleMarkerClusterLoad();
      });
    } else {
      initMap();
      handleMarkerClusterLoad();
    }
  };

  const exitHandler = () => {
    if (!document.webkitIsFullScreen && !document.mozFullScreen && !document.msFullscreenElement) {
      handleFullscreenExit();
    }
  };

  const handleMarkerClusterLoad = () => {
    setIsMarkerClusterLoaded(true);
    if (onMarkerClusterLoad) {
      onMarkerClusterLoad(window.markerClusterer?.MarkerClusterer);
    }
  };

  // CHANGED: Now async to wait for libraries
  const initMap = async () => {
    if (googleMapRef.current && window.google) {
      try {
        // 1. Import the Maps library (Fixes "Map is not a constructor")
        const { Map } = await window.google.maps.importLibrary("maps");
        
        // 2. Also preload the Marker library so children components are safe
        await window.google.maps.importLibrary("marker");

        // 3. Pass the specific Map constructor to our creation function
        const googleMap = createGoogleMap(Map);
        setMap(googleMap);
        setIsMapLoaded(true);

        if (onMapClick) {
          googleMap.addListener('click', () => {
            onMapClick(googleMap);
          });
        }

        if (onMapLoad) {
          onMapLoad(googleMap);
        }
      } catch (error) {
        console.error("Google Maps Library Import Error:", error);
      }
    }
  };

  const handleFullScreen = id => {
    setShowFullScreenIcon(false);
    if (onFullScreenButtonClick) {
      let mapElement = document.getElementById(id);
      onFullScreenButtonClick(mapElement);
    }
  };

  const handleFullscreenExit = () => {
    setShowFullScreenIcon(true);
    if (onFullScreenExit) {
      onFullScreenExit();
    }
  };

  // CHANGED: Accepts MapConstructor to ensure we use the loaded class
  const createGoogleMap = (MapConstructor) => {
    let completeMapOptions = {
      ...mapOptions,
      mapId: mapOptions.mapId || mapId, 
      zoom: 16,
      disableDefaultUI: true,
      scaleControl: true,
      zoomControl: window.innerWidth > 768,
      scrollwheel: true,
      scrollWheelZoom: 'center',
    };

    // Use the constructor passed from initMap
    return new MapConstructor(googleMapRef.current, completeMapOptions);
  };

  const mapContainerCssStyleObj = { width: '100%', height: '80vh' };
  const fullScreenIconStyle = { marginLeft: '-20px', marginTop: '-50px', position: 'relative', marginBottom: '50px', zIndex: 100000, color: '#085488', img: { height: '15px', width: '15px', marginRight: '10px' } };

  return (
    <div id="mapContainer">
      <div id="google-map" ref={googleMapRef} className={showFullScreenIcon ? mapContainerCssStyle : mapContainerStyleFullScreen} style={mapContainerCssStyleObj}>
        <MapContext.Provider value={map}>{children}</MapContext.Provider>
      </div>
      {fullScreenIcon && showFullScreenIcon && (
        <div className={'d-md-none'} id="iconFullscreen" style={fullScreenIconStyle} onClick={() => handleFullScreen('mapContainer')}>
          <img src={fullScreenIcon} style={fullScreenIconStyle.img} alt="full screen" />
          <span>Click here to expand</span>
        </div>
      )}
    </div>
  );
};

Map.propTypes = {
  mapId: PropTypes.string,
  mapContainerCssStyle: PropTypes.string,
  mapContainerStyleFullScreen: PropTypes.string,
  mapOptions: PropTypes.object,
  apiKey: PropTypes.string.isRequired,
  onMapClick: PropTypes.func,
  onMapLoad: PropTypes.func,
  onMarkerClusterLoad: PropTypes.func,
  onFullScreenButtonClick: PropTypes.func,
  onFullScreenExit: PropTypes.func,
  fullScreenIcon: PropTypes.string,
  children: PropTypes.node
};

export default Map;